import {useCallback, useEffect, useState} from 'react';

export function useLocalStorageState<T>(key: string, defaultValue: T) {
  const getValueFromLocalStorage = useCallback(() => {
    // No localStorage during SSR.
    if (typeof localStorage === 'undefined') return defaultValue;

    // If the local storage value is empty, return the default value.
    const localStorageValue = localStorage.getItem(key);
    if (localStorageValue === null) return defaultValue;

    // Otherwise try to json parse the local storage value, and use the
    // default value as a fallback in case the local storage value got corrupted.
    try {
      return JSON.parse(localStorageValue) as T;
    } catch {
      return defaultValue;
    }
  }, [key]);

  // Start from the SSR default so the first client render matches the server-rendered
  // HTML; the effect below loads the real localStorage value after mount. Reading
  // localStorage in the initializer causes a hydration mismatch whenever the stored
  // value differs from the default.
  const [value, setValue] = useState(defaultValue);

  const setValueAndSaveToLocalStorage = useCallback(
    (newValue: T) => {
      if (newValue !== null) {
        localStorage.setItem(key, JSON.stringify(newValue));
        setValue(newValue);
      } else {
        localStorage.removeItem(key);
        setValue(defaultValue);
      }
    },
    [key],
  );

  useEffect(() => {
    // Update internal value if key prop changes.
    setValue(getValueFromLocalStorage());
  }, [key]);

  return [value, setValueAndSaveToLocalStorage] as const;
}
